/**
 * Массовые операции (§5.7).
 *
 * Выделение построчное: чекбокс в gutter, Shift — диапазон, Ctrl/Cmd —
 * добавить, чекбокс в шапке — «Выделить все N показанных» (формулировка
 * обязательна: именно показанных, а не всех в канале).
 *
 * Результат операции — N обычных правок в черновике, а не отдельная сущность:
 * ревью, Ctrl+Z и подтверждение работают с ними так же, как с ручными.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Brand } from '@shared/types.ts'
import { asOneOperation } from '../../../lib/api.js'
import type { ColumnDef } from '../table/columns.js'
import { readCell, type ValueContext } from '../table/values.js'

/** Одновременных запросов на массовой правке: больше — и сервер начинает ждать. */
const BATCH = 6

export interface BulkCensus {
  total: number
  empty: number
  filled: number
}

export interface BulkEditApi {
  selected: ReadonlySet<number>
  count: number
  isSelected: (brandId: number) => boolean
  toggle: (brandId: number, modifiers: { shift: boolean; meta: boolean }) => void
  /** Протягивание: зажали на чекбоксе и повели по строкам. */
  dragStart: (brandId: number) => void
  dragOver: (brandId: number) => void
  dragging: boolean
  selectAllShown: (checked: boolean) => void
  clear: () => void
  allSelected: boolean
  someSelected: boolean
  /** Сколько выбранных брендов уже заполнено — для предохранителя (§5.7.4). */
  census: (column: ColumnDef) => BulkCensus
  /** Применить значение. Возвращает число реально записанных правок. */
  apply: (column: ColumnDef, value: unknown, mode: 'empty' | 'all') => Promise<number>
  running: boolean
}

export interface BulkEditInput {
  /** Показанные бренды в порядке снимка: по нему работает Shift-диапазон. */
  shownBrands: readonly Brand[]
  ctx: ValueContext
  /** Запись одной правки — та же, что в ручной правке ячейки. */
  write: (brand: Brand, column: ColumnDef, value: unknown) => Promise<void>
}

export function useBulkEdit({ shownBrands, ctx, write }: BulkEditInput): BulkEditApi {
  const [selected, setSelected] = useState<ReadonlySet<number>>(() => new Set<number>())
  const [running, setRunning] = useState(false)
  const anchor = useRef<number | null>(null)
  /* Протягивание: запоминаем, что было выделено до начала жеста, и на каждой
     новой строке пересобираем выбор как «было + диапазон». Так обратный ход
     мышью снимает лишнее, а не накапливает. */
  const drag = useRef<
    { from: number; base: ReadonlySet<number>; moved: boolean; mode: 'add' | 'remove' } | null
  >(null)
  const justDragged = useRef(false)
  const [dragging, setDragging] = useState(false)

  const order = useMemo(() => shownBrands.map((brand) => brand.id), [shownBrands])

  const toggle = useCallback((brandId: number, modifiers: { shift: boolean; meta: boolean }) => {
    if (justDragged.current) { justDragged.current = false; return }
    setSelected((previous) => {
      const next = new Set(previous)

      // Shift — диапазон от последнего отмеченного; Ctrl/Cmd и обычный клик
      // по чекбоксу работают одинаково: чекбокс читается как «отметить».
      if (modifiers.shift && anchor.current !== null) {
        const from = order.indexOf(anchor.current)
        const to = order.indexOf(brandId)
        if (from >= 0 && to >= 0) {
          const [start, end] = from <= to ? [from, to] : [to, from]
          for (let i = start; i <= end; i += 1) {
            const id = order[i]
            if (id !== undefined) next.add(id)
          }
          anchor.current = brandId
          return next
        }
      }

      if (next.has(brandId)) next.delete(brandId)
      else next.add(brandId)
      anchor.current = brandId
      return next
    })
  }, [order])

  /**
   * Выбор после протягивания: то, что было, плюс-минус пройденный диапазон.
   *
   * Направление жеста решает первая строка: начали с невыделенной — весь
   * проход отмечается, начали с выделенной — снимается. Так один и тот же
   * жест и ставит галочки, и убирает, а обратный ход возвращает всё как было.
   */
  const rangeFrom = useCallback((
    from: number, to: number, base: ReadonlySet<number>, mode: 'add' | 'remove',
  ) => {
    const next = new Set(base)
    const start = order.indexOf(from)
    const end = order.indexOf(to)
    if (start < 0 || end < 0) return next
    const [a, b] = start <= end ? [start, end] : [end, start]
    for (let i = a; i <= b; i += 1) {
      const id = order[i]
      if (id === undefined) continue
      if (mode === 'add') next.add(id)
      else next.delete(id)
    }
    return next
  }, [order])

  /* Нажатие ничего не выделяет: обычный клик по чекбоксу должен работать как
     раньше. Жест начинается, только когда курсор вошёл в соседнюю строку. */
  const dragStart = useCallback((brandId: number) => {
    setSelected((previous) => {
      drag.current = {
        from: brandId,
        base: previous,
        moved: false,
        mode: previous.has(brandId) ? 'remove' : 'add',
      }
      return previous
    })
    setDragging(true)
  }, [])

  const dragOver = useCallback((brandId: number) => {
    const state = drag.current
    if (!state || brandId === state.from) return
    state.moved = true
    setSelected(rangeFrom(state.from, brandId, state.base, state.mode))
    anchor.current = brandId
  }, [rangeFrom])

  /* Кнопку отпустили где угодно — жест закончен. Слушаем окно, а не строку:
     палец или курсор часто уходит за пределы таблицы. */
  useEffect(() => {
    if (!dragging) return
    const stop = () => {
      /* Если курсор уехал по строкам — это было протягивание, и click,
         который придёт следом, выделение трогать не должен. */
      justDragged.current = drag.current?.moved === true
      drag.current = null
      setDragging(false)
    }
    window.addEventListener('pointerup', stop)
    window.addEventListener('pointercancel', stop)
    return () => {
      window.removeEventListener('pointerup', stop)
      window.removeEventListener('pointercancel', stop)
    }
  }, [dragging])

  const selectAllShown = useCallback((checked: boolean) => {
    setSelected(checked ? new Set(order) : new Set<number>())
    anchor.current = null
  }, [order])

  const clear = useCallback(() => {
    setSelected(new Set<number>())
    anchor.current = null
  }, [])

  const picked = useMemo(
    () => shownBrands.filter((brand) => selected.has(brand.id)),
    [shownBrands, selected],
  )

  const census = useCallback((column: ColumnDef): BulkCensus => {
    let empty = 0
    let filled = 0
    for (const brand of picked) {
      const cell = readCell(ctx, column, { kind: 'brand', key: `b${brand.id}`, brand })
      if (!cell) continue
      if (cell.empty) empty += 1
      else filled += 1
    }
    return { total: empty + filled, empty, filled }
  }, [picked, ctx])

  const apply = useCallback(async (column: ColumnDef, value: unknown, mode: 'empty' | 'all') => {
    const targets = picked.filter((brand) => {
      const cell = readCell(ctx, column, { kind: 'brand', key: `b${brand.id}`, brand })
      if (!cell) return false
      if (mode === 'empty' && !cell.empty) return false
      // Повторная запись того же значения всё равно удалилась бы из черновика.
      return cell.value !== value
    })

    if (targets.length === 0) return 0
    setRunning(true)
    try {
      /* Вся простановка — одна операция: человек сделал одно движение, и
         «Шаг назад» обязан снять её целиком, а не по бренду за нажатие. */
      await asOneOperation(async () => {
        for (let i = 0; i < targets.length; i += BATCH) {
          const slice = targets.slice(i, i + BATCH)
          await Promise.all(slice.map((brand) => write(brand, column, value)))
        }
      })
      return targets.length
    } finally {
      setRunning(false)
    }
  }, [picked, ctx, write])

  return {
    selected,
    count: selected.size,
    isSelected: (brandId: number) => selected.has(brandId),
    toggle,
    dragStart,
    dragOver,
    dragging,
    selectAllShown,
    clear,
    allSelected: order.length > 0 && order.every((id) => selected.has(id)),
    someSelected: selected.size > 0,
    census,
    apply,
    running,
  }
}
